import React, { useState, useEffect } from 'react';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useTIN } from '@src/state/taxInformation';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { usePAARegistration } from '@src/state/PAARegistration';
import { UserRegistrationShell } from '@src/common/UserRegistrationShell';
import { AccountInfo } from '@src/common/AccountInfo';
import { JobFunction } from '@src/common/JobFunction';
import { APIAccess, APIAccessV2 } from '@src/common/APIAccess';
import { useEditing } from '@src/state/editing/hooks';
import { useOnloading } from '@src/state/landing/index';
import { FLOW_ID } from '@src/common/AppConstants';
import {
  adobeInteraction,
  getOrgType,
  isValidPhone,
} from '@src/common/helpers';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useOnStepper } from '@src/state/stepper';
import { StepperV2 } from '@src/common/StepperV2';
import { useUser } from '@src/state/user';
import { useVerifyClaim } from '@src/state/verifyClaim';
import { jobFunctions } from '@src/app/constants/jobFunctions';
import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { AccessAdministrator } from '@src/common/AccessAdministrator';
import { PAAReviewConfirm } from './PAAReviewConfirm';
import { PAApageData } from './PAAStepsMapper';
import { CompanyVerification } from './CompanyVerification';
import { WaitingView } from './VerifyClaim/WaitingView';
import { VerificationPending } from './CompanyVerification/VerificationPending';
import { VerifyClaimV3 } from './VerifyClaim/VerifyClaimV3/VerifyClaimV3';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { JobFunctionV2 } from '@src/common/JobFunctionV2';

const BoxHeader = styled('div', {
  position: 'relative',
  paddingLeft: '20px',
  marginBottom: '10px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const InputDescription = styled('p', {
  fontSize: '16px',
  fontWeight: 600,
  paddingLeft: '20px',
  paddingRight: '35px',
  fontFamily: '$primary',
});

const AttestationLink = styled('p', {
  marginLeft: '20px',
  color: '$info1',
  marginBottom: '15px',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'none',
    color: '$info1',
  },
});

const InfoLink = styled('span', {
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'none',
  },
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  marginTop: '30px',
  padding: '20px 10px',
});

const AlertDisplay = styled('div', {
  marginBottom: '2%',
});

const AlertDescription = styled('div', {
  fontSize: 14,
  fontWeight: 600,
});

const FlyoutMainLabel = styled('p', {
  fontWeight: '900',
});

const FlyoutText = styled('p', {
  fontWeight: 'normal',
});

const ValidationErrorSnackBar = () => {
  return (
    <div style={{ margin: '0 auto' }}>
      <React.Fragment>
        <AlertDisplay style={{ marginBottom: '15px' }}>
          <Alert
            variant="error"
            title="Information provided does not match the information on file."
          >
            <AlertDescription>Please try again.</AlertDescription>
          </Alert>
        </AlertDisplay>
      </React.Fragment>
    </div>
  );
};

export const PAAFlowSelection = () => {
  const { token } = useParams();
  const { TINNumber, TINInfo } = useTIN.state();
  const [flyoutModalOpen, setFlyoutModalOpen] = useState(false);
  const { isEditing } = useEditing.state();
  const { currentFlow } = useOnloading.state();
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  const tinInfo = TINInfo == null ? { corpName: 'ABC Hopsital' } : TINInfo;

  const { navigate } = useRouter();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'paa registration';
      const section2 = 'home';
      const pageName = `digitalsecurity:${section1}:${section2}`;

      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: tinInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3: '',
      });
      adobeTaggingEvent('PAA_REGISTRATION_STARTS', {
        jobName: 'paa registration',
      });
    }
  }, []);

  return (
    <React.Fragment>
      <div>
        <Alert
          variant="info"
          title={
            !isAPIMarketPlaceUser
              ? `We did not find a Primary Access Administrator for ${tinInfo.corpName} tax ID #: ${TINNumber}. A Primary Access Administrator is required in order to register.`
              : `We did not find a Secret Keeper for ${tinInfo.corpName} tax ID #: ${TINNumber}. Secret Keeper is required in order to register.`
          }
        />
      </div>
      <MainContainer>
        <BoxHeader>
          <BoxHeaderText>How would you like to proceed?</BoxHeaderText>
        </BoxHeader>
        <InputDescription>
          {!isAPIMarketPlaceUser
            ? `Primary Access Administrator (PAA) functions as the primary link between your organization and UnitedHealthcare.`
            : `The Secret Keeper serves as the primary contact between your organization and the UnitedHealthcare API Marketplace. If you are the first person to register for your organization, you must attest as the Secret Keeper in order to continue. This role may be given to another user within your organization at a later time if needed.`}
          <InfoLink
            onClick={() => {
              return setFlyoutModalOpen(true);
            }}
          >
            <IconSymbol icon="help" size="14px" />
          </InfoLink>
        </InputDescription>
        <AttestationLink
          onClick={() => {
            return navigate(`${PAApageData[currentFlow][0]?.url}/${token}`);
          }}
        >
          {!isAPIMarketPlaceUser ? (
            <strong>Attest that I am the Primary Access Administrator</strong>
          ) : (
            <strong>Attest that I am the Secret Keeper</strong>
          )}{' '}
          &nbsp;
          <IconSymbol icon="arrow_forward" size={14} color="$primary1" />
        </AttestationLink>
        <AttestationLink
          onClick={() => {
            return navigate(`/PAADesignate/${token}`);
          }}
        >
          {!isAPIMarketPlaceUser ? (
            <strong>
              Identify the Primary Access Administrator for {tinInfo.corpName}
            </strong>
          ) : (
            <strong>Identify the Secret Keeper for {tinInfo.corpName}</strong>
          )}{' '}
          &nbsp;
          <IconSymbol icon="arrow_forward" size={14} color="$primary1" />
        </AttestationLink>
      </MainContainer>
      {!isEditing && (
        <Flex.Content>
          <div style={{ marginTop: '20px' }}>
            <BackButton
              onClick={() => {
                adobeInteraction(
                  'paa registration home',
                  'back',
                  currentFlow,
                  featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
                );
                navigate(`/TINValidation/${currentFlow}/${token}`);
              }}
            >
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          </div>
        </Flex.Content>
      )}
      <Drawer
        size="lg"
        isOpen={flyoutModalOpen}
        placement="right"
        showCloseButton={false}
        onClose={() => {
          return setFlyoutModalOpen(false);
        }}
        width="275px"
      >
        <Modal.Section>
          <FlyoutMainLabel>
            {!isAPIMarketPlaceUser
              ? 'What is a Primary Access Administrator?'
              : 'What is a Secret Keeper?'}
          </FlyoutMainLabel>
          <FlyoutText>
            {!isAPIMarketPlaceUser
              ? 'Primary Access Administrator (PAA) functions as the primary link between your organization and UnitedHealthcare.'
              : 'The Secret Keeper functions as the primary technical link between your organization and API Marketplace.'}
          </FlyoutText>
          <FlyoutText>
            {!isAPIMarketPlaceUser
              ? `PAA is responsible for managing security, setting up and maintaining user accounts, updating organization information, and performing other administrative tasks in the UnitedHealthcare Provider Portal.`
              : `Secret Keeper is solely responsible for maintaining your organization’s API credentials and generating new API credentials, as needed.`}
          </FlyoutText>
          <FlyoutText>
            {!isAPIMarketPlaceUser
              ? null
              : `They will have the ability to view your organizations APIs on API Marketplace and request additional APIs.`}
          </FlyoutText>
        </Modal.Section>
      </Drawer>
    </React.Fragment>
  );
};

export const PAARegistration = () => {
  const form = useForm();
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  const registrationSteps = {
    adminAttestation: 'Access Administrator',
    companyVerification: 'Company Verification',
    accountInfo: 'Account Information',
    jobFunction: 'Job Functions',
    apiAccess: 'API Access',
    reviewConfirm: 'Review & Confirm',
  };
  const { navigate } = useRouter();
  const { registrationStep, token } = useParams();
  const [activeStep, setActiveStep] = useState('');
  const [cancelLink, setCancelLink] = useState(true);
  const [steps, updateSteps] = useState();
  const { setStepper, setCurrentStep, setCompleteStep } =
    useOnStepper.actions();

  const { setPhoneNumber, resetState, setJobFunctions } =
    usePAARegistration.actions();
  const { phone, phoneExt, selectedJobFunctions } = usePAARegistration.state();
  const { TINNumber, TINInfo = { paaInfo: null } } = useTIN.state();
  const { setIsEditing } = useEditing.actions();
  const { currentFlow } = useOnloading.state();
  const [showClaimError, setShowClaimError] = useState(false);
  const [showMedicaidError, setShowMedicaidError] = useState('');

  const [currentStepper, setCurrentStepper] = useState();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { reduxMainRadio, address } = useVerifyClaim.state();
  const { state: locationState } = useLocation();

  const navigationAllowed = () => {
    if (activeStep === 'Account Information') {
      form.validate('user.phone');
      if (!isValidPhone(phone)) {
        return false;
      }
    }
    return true;
  };

  const handleSetActiveStep = (step, stepper) => {
    if (navigationAllowed()) {
      const ans = PAApageData[currentFlow].find((el) => {
        return el.name === step;
      });
      navigate(`${ans.url}/${token}`);
      setActiveStep(ans.name);
      setCurrentStepper(stepper);
    }
  };

  const handleStopAll = () => {
    const newSteps = [...PAApageData[currentFlow]];
    const ans = PAApageData[currentFlow].findIndex((el) => {
      return el.name === activeStep;
    });
    newSteps.splice(ans + 1, newSteps.length);
    setCancelLink(false);
    setStepper({ data: newSteps, save: true });
    setCompleteStep(1);
    updateSteps(newSteps);
  };

  const handleDisableCancel = () => {
    setCancelLink(false);
  };

  const handleRouteStep = (step, url = null) => {
    setActiveStep(step);
    if (url) {
      navigate(`${url}/${token}`);
    } else {
      const path =
        url ||
        Object.keys(registrationSteps).find((key) => {
          return registrationSteps[key] === step;
        });
      navigate(`/PAARegistration/${path}/${token}`);
    }
  };

  const handleNext = () => {
    adobeInteraction(
      activeStep,
      'next',
      currentFlow,
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
    );
    const currentStep = steps.findIndex((el) => {
      return el.name === activeStep;
    });
    setCompleteStep(currentStep + 1);

    handleRouteStep(steps[currentStep + 1].name, steps[currentStep + 1].url);

    if (activeStep === 'Company Verification') {
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        adobeTaggingEvent('PAA_REGISTRATION_COMPLETION', {
          jobName: 'paa registration',
          companyVerificationMethod: reduxMainRadio,
          providerUuid: userUUID,
          registrationTaxId: TINNumber,
        });
      }
    }

    if (currentStep + 1 === steps?.length - 1) setIsEditing(false);
  };

  const handleBack = () => {
    adobeInteraction(
      activeStep,
      'back',
      currentFlow,
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
    );
    const currentStep = steps.findIndex((el) => {
      return el.name === activeStep;
    });
    if (currentStep === 0) {
      const isTechnician = currentFlow.includes('technician');
      if (isAPIMarketPlaceUser && isTechnician) {
        navigate(`/TINValidation/${currentFlow}/${token}`);
      } else {
        navigate(`/PAARegistration/home/${token}`);
      }
      resetState();
      setActiveStep('');
    } else {
      handleRouteStep(steps[currentStep - 1].name, steps[currentStep - 1].url);
    }
  };

  useEffect(() => {
    if (
      (currentFlow !== FLOW_ID.API_MARKETPLACE_PROVIDER_TECHNICIAN &&
        registrationStep === 'adminAttestation') ||
      (steps?.length === 2 && registrationStep === 'accountInfo')
    ) {
      setCancelLink(true);
      setCompleteStep(1);
      if (registrationStep === 'adminAttestation' && steps?.length === 2) {
        setStepper({ data: PAApageData[currentFlow], save: true });
        setCurrentStepper(PAApageData[currentFlow]);
      } else {
        const stepper = currentStepper || PAApageData[currentFlow];
        setStepper({ data: stepper, save: true });
      }
      updateSteps(PAApageData[currentFlow]);

      if (registrationStep === 'accountInfo') {
        handleRouteStep(registrationSteps.adminAttestation);
        setCompleteStep(1);
      }
    }

    if (
      currentFlow.includes('API marketplace') &&
      registrationStep === 'adminAttestation'
    ) {
      setCompleteStep(1);
      setActiveStep('Attestation');
      setCurrentStep('Attestation');
    } else {
      setActiveStep(registrationSteps[registrationStep] || '');
      setCurrentStep(registrationSteps[registrationStep] || '');
    }
    if (showClaimError) setShowClaimError(false);
    if (registrationStep === 'verificationPending') setCancelLink(false);
  }, [registrationStep]);

  useEffect(() => {
    setStepper({ data: PAApageData[currentFlow], save: true });
    updateSteps(PAApageData[currentFlow]);
    if (!activeStep) {
      if (locationState && locationState.startStep === 'adminAttestation') {
        setActiveStep('Attestation');
      } else {
        navigate(`/PAARegistration/home/${token}`);
      }
      navigate(`/PAARegistration/home/${token}`);
    }
  }, []);

  const getVerifyClaim = () => {
    return (
      <VerifyClaimV3
        form={form}
        handleNext={handleNext}
        handleBack={handleBack}
        handleStopAll={handleStopAll}
        handleDisableCancel={handleDisableCancel}
        handleError={(bool) => {
          setShowClaimError(bool);
        }}
        handleMedicaidError={(code) => {
          setShowMedicaidError(code);
        }}
        showMedicaidError={showMedicaidError}
        address={address}
      />
    );
  };
  const renderStep = () => {
    switch (registrationStep) {
      case 'home':
        return <PAAFlowSelection />;
      case 'adminAttestation':
        return (
          <AccessAdministrator
            handleNext={handleNext}
            handleBack={handleBack}
          />
        );
      case 'companyVerification':
        return currentFlow === FLOW_ID.PROVIDER ||
          currentFlow === FLOW_ID.API_MARKETPLACE_PROVIDER ? (
          getVerifyClaim()
        ) : (
          <CompanyVerification
            currentFlow={currentFlow}
            completeStep={() => {
              return setCompleteStep(2);
            }}
            handleBack={handleBack}
          />
        );
      case 'accountInfo':
        return (
          <AccountInfo
            handleNext={handleNext}
            handleBack={handleBack}
            setPhoneNumber={setPhoneNumber}
            phone={phone}
            phoneExt={phoneExt}
          />
        );
      case 'jobFunction':
        return featureToggles.getRow('US9430644_JOB_FUNCTION_UPDATE').active ? (
          <JobFunctionV2
            jobFunctions={jobFunctions}
            selectedJobFunctions={selectedJobFunctions}
            setJobFunctions={setJobFunctions}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        ) : (
          <JobFunction
            jobFunctions={jobFunctions}
            selectedJobFunctions={selectedJobFunctions}
            setJobFunctions={setJobFunctions}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        );
      case 'apiAccess':
        return featureToggles.getRow('US8482146_SUPER_CONNECTOR')?.active ? (
          <APIAccessV2 handleBack={handleBack} handleNext={handleNext} />
        ) : (
          <APIAccess handleBack={handleBack} handleNext={handleNext} />
        );
      case 'reviewConfirm':
        return (
          <PAAReviewConfirm paaInfo={TINInfo.paaInfo} handleBack={handleBack} />
        );
      case 'waitingView':
        return <WaitingView />;
      case 'verificationPending':
        return <VerificationPending />;
      default:
        return null; // or redirect to a default step
    }
  };

  return (
    <React.Fragment>
      <UserRegistrationShell
        showCancelLink={cancelLink}
        activeStep={activeStep}
      >
        {activeStep !== '' && <StepperV2 setActiveStep={handleSetActiveStep} />}
        {showClaimError && <ValidationErrorSnackBar />}
        {renderStep()}
      </UserRegistrationShell>
    </React.Fragment>
  );
};
